/**
 * NOTE: This code belongs to Kangaroo Rewards. Unauthorized use is prohibited
 */

@file:Suppress("unused", "MemberVisibilityCanBePrivate", "EXPERIMENTAL_API_USAGE")
@file:OptIn(ExperimentalJsExport::class)
package features.publicQrCodeScan

import kangaroorewards.appsdk.core.api.Api
import kangaroorewards.appsdk.core.domain.Result
import kangaroorewards.appsdk.core.domain.Result.Idle
import kangaroorewards.appsdk.core.domain.Result.Loading
import kangaroorewards.appsdk.core.domain.SerializedResult
import kangaroorewards.appsdk.core.domain.toJsonResult
import kangaroorewards.appsdk.core.utils.CFlow
import kangaroorewards.appsdk.core.utils.asCFlow
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.serialization.InternalSerializationApi
import kotlin.js.ExperimentalJsExport
import features.publicQrCodeScan.internal.ScanningPublicQrCodeUseCase
import features.publicQrCodeScan.internal.ScanningPublicQrCodeUseCase.Params
import features.publicQrCodeScan.models.PublicQrCodeScanResponseModel
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import features.publicQrCodeScan.models.ScanPublicQrCodeRequest

typealias ScanningPublicQrCodeState = CFlow<ScanningPublicQrCodeUseCaseResult?>

typealias ScanningPublicQrCodeUseCaseResult = Result<PublicQrCodeScanResponseModel>

fun ScanningPublicQrCodeUseCaseResult.serializeScanningPublicQrCodeApiResult(): SerializedResult<String> {
    return this.toJsonResult<PublicQrCodeScanResponseModel>()
}

/**
 * Serializes [ScanningPublicQrCodeState]'s data and returns a new
 * [CFlow] containing data serialized as a JSON [String].
 */
@OptIn(ExperimentalCoroutinesApi::class)
fun ScanningPublicQrCodeState.serializeScanningPublicQrCodeState(): CFlow<SerializedResult<String>?> {
    return this.mapLatest {
        it?.toJsonResult<PublicQrCodeScanResponseModel>()
    }.asCFlow()
}

/**
 * Scan a QR code. [Api] class
 */
class ScanningPublicQrCodeApi : Api() {

    val scanningPublicQrCodeState: ScanningPublicQrCodeState
        get() = _scanningPublicQrCodeState.asCFlow()

    companion object {
        private val _scanningPublicQrCodeState: MutableStateFlow<ScanningPublicQrCodeUseCaseResult> = MutableStateFlow(Idle())
    }

    private val scanningPublicQrCodeUseCase = ScanningPublicQrCodeUseCase()

    /**
     * Scan a QR code.
     */
    suspend fun scanPublicQrCode(
        overrideHeaders: Map<String, String>? = null,
        scanPublicQrCodeRequest: ScanPublicQrCodeRequest
    ): Result<PublicQrCodeScanResponseModel> {
        /* set state to Loading as soon as function is called */
        _scanningPublicQrCodeState.value = Loading()
        val result = this.scanningPublicQrCodeUseCase(
            Params(
                overrideHeaders = overrideHeaders,
                scanPublicQrCodeRequest = scanPublicQrCodeRequest
            )
        ) { result ->
            _scanningPublicQrCodeState.value = result
            return@scanningPublicQrCodeUseCase result
        }
        return result
    }

    /**
    * Scan a QR code.
    *
    * This method is used by the Native artifact when dealing with object body params
    */
    suspend fun scanPublicQrCode(
        overrideHeaders: Map<String, String>? = null,
        methods: Map<String, Any>
    
    ): Result<PublicQrCodeScanResponseModel> {
        return scanPublicQrCode(
            overrideHeaders = overrideHeaders,
            scanPublicQrCodeRequest = Json.decodeFromString(methods.getValue("scanPublicQrCodeRequest") as String)
        )
    }
}
